import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Time.module.scss";
import { ClickAwayListener, Popper } from "@mui/material";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import withLabelAndError from "../withLabelAndError";
import { combineClasses } from "@/lib/utils";

export interface TimeInputProps<T extends FieldValues = FieldValues> {
  control?: Control<T>;
  register?: UseFormRegister<T>;
  validations?: RegisterOptions<T, Path<T>>;
  name: Path<T>;
  placeholder?: string;
  min?: string;
  max?: string;
  step?: number;
  [key: string]: unknown;
}

const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const PERIODS = ["AM", "PM"] as const;
type Period = (typeof PERIODS)[number];

const padValue = (value: number) => String(value).padStart(2, "0");

const buildMinuteOptions = (step = 60) => {
  const minuteStep = step > 0 && step % 60 === 0 ? step / 60 : 1;
  const minutes: string[] = [];

  for (let minute = 0; minute < 60; minute += minuteStep) {
    minutes.push(padValue(minute));
  }

  return minutes;
};

const parseTimeValue = (value?: string) => {
  if (!value) return null;

  const matchedTime = value.match(/(\d{1,2}):(\d{2})/);

  if (!matchedTime) return null;

  const [, hourValue, minuteValue] = matchedTime;
  const hour = Number(hourValue);
  const minute = Number(minuteValue);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;

  return {
    hour: padValue(hour % 12 || 12),
    minute: padValue(minute),
    period: (hour >= 12 ? "PM" : "AM") as Period,
  };
};

const formatTimeValue = ({
  hour,
  minute,
  period,
}: {
  hour: string;
  minute: string;
  period: Period;
}) => {
  let hoursIn24Format = Number(hour) % 12;

  if (period === "PM") {
    hoursIn24Format += 12;
  }

  return `${padValue(hoursIn24Format)}:${padValue(Number(minute))}`;
};

const formatDisplayTime = (value?: string, placeholder = "-- : -- --") => {
  const parsedTime = parseTimeValue(value);

  if (!parsedTime) return placeholder;

  return `${parsedTime.hour} : ${parsedTime.minute} ${parsedTime.period}`;
};

const isTimeWithinBounds = (value: string, min?: string, max?: string) => {
  if (min && value < min) return false;
  if (max && value > max) return false;
  return true;
};

const ClockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 7V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ControlledTimeFieldProps {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name: string;
  placeholder?: string | undefined;
  min?: string | undefined;
  max?: string | undefined;
  step?: number | undefined;
  disabled?: boolean | undefined;
}

const ControlledTimeField = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  min,
  max,
  step = 60,
  disabled,
}: ControlledTimeFieldProps) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const minuteOptions = useMemo(() => buildMinuteOptions(step), [step]);
  const fallbackSelection = parseTimeValue(min) ?? {
    hour: "12",
    minute: minuteOptions[0] || "00",
    period: "AM" as Period,
  };
  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState(parseTimeValue(value) ?? fallbackSelection);

  useEffect(() => {
    setSelection(parseTimeValue(value) ?? fallbackSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const closePicker = () => {
    setIsOpen(false);
    onBlur?.();
  };

  const openPicker = () => {
    if (disabled) return;

    setSelection(parseTimeValue(value) ?? fallbackSelection);
    setIsOpen(true);
  };

  const togglePicker = () => {
    if (isOpen) {
      closePicker();
      return;
    }

    openPicker();
  };

  const updateSelection = (key: "hour" | "minute" | "period", nextValue: string) => {
    const nextSelection = {
      ...selection,
      [key]: nextValue,
    } as typeof selection;
    const formattedValue = formatTimeValue(nextSelection);

    if (!isTimeWithinBounds(formattedValue, min, max)) return;

    setSelection(nextSelection);
    onChange(formattedValue);
  };

  const clearTime = () => {
    onChange("");
    closePicker();
  };

  const renderOptionButton = (type: "hour" | "minute" | "period", option: string) => {
    const candidate = formatTimeValue({
      ...selection,
      [type]: option,
    } as typeof selection);
    const isDisabled = !isTimeWithinBounds(candidate, min, max);
    const isSelected = selection[type] === option;

    return (
      <button
        key={`${type}-${option}`}
        type="button"
        className={combineClasses(
          styles.timeOption,
          isSelected && styles.selectedOption,
          isDisabled && styles.disabledOption,
        )}
        onClick={() => updateSelection(type, option)}
        disabled={isDisabled}
        aria-pressed={isSelected}
      >
        {option}
      </button>
    );
  };

  return (
    <div className={styles.pickerWrapper}>
      <button
        ref={triggerRef}
        id={name}
        type="button"
        className={combineClasses(
          styles.inputField,
          isOpen && styles.isOpen,
          !value && styles.placeholderState,
        )}
        onClick={togglePicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className={combineClasses(styles.valueText, !value && styles.placeholderText)}>
          {formatDisplayTime(value, placeholder || "-- : -- --")}
        </span>
        <span className={styles.icon}>
          <ClockIcon />
        </span>
      </button>

      <Popper open={isOpen} anchorEl={triggerRef.current} placement="top-start" className={styles.popper}>
        <ClickAwayListener onClickAway={closePicker}>
          <div className={styles.popup} role="dialog" aria-label="Choose time">
            <div className={styles.popupHeader}>
              <span className={styles.popupTitle}>Select time</span>
              <span className={styles.popupValue}>
                {formatDisplayTime(value || formatTimeValue(selection), "-- : -- --")}
              </span>
            </div>

            <div className={styles.timeColumns}>
              <div className={styles.timeColumn}>
                <span className={styles.columnLabel}>Hour</span>
                <div className={styles.optionList}>
                  {HOURS.map((hour) => renderOptionButton("hour", hour))}
                </div>
              </div>

              <div className={styles.timeColumn}>
                <span className={styles.columnLabel}>Minute</span>
                <div className={styles.optionList}>
                  {minuteOptions.map((minute) => renderOptionButton("minute", minute))}
                </div>
              </div>

              <div className={styles.timeColumn}>
                <span className={styles.columnLabel}>Period</span>
                <div className={styles.optionList}>
                  {PERIODS.map((period) => renderOptionButton("period", period))}
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <button type="button" className={styles.footerButton} onClick={clearTime}>
                Clear
              </button>
              <button
                type="button"
                className={combineClasses(styles.footerButton, styles.footerButtonPrimary)}
                onClick={closePicker}
              >
                Done
              </button>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

const TimeInput = <T extends FieldValues = FieldValues>({
  control,
  register,
  validations,
  name,
  placeholder,
  min,
  max,
  step = 60,
  ...rest
}: TimeInputProps<T>) => {
  const formatTime = (value: unknown) => {
    if (!value) return "";

    if (typeof value === "string") {
      const matchedTime = value.match(/(\d{1,2}):(\d{2})/);

      if (matchedTime) {
        const [, hourValue, minuteValue] = matchedTime;
        return `${padValue(Number(hourValue))}:${minuteValue}`;
      }
    }

    if (value instanceof Date) {
      return `${padValue(value.getHours())}:${padValue(value.getMinutes())}`;
    }

    return "";
  };

  const renderNativeInput = (field: Record<string, unknown> = {}) => (
    <input
      {...field}
      {...rest}
      id={name}
      type="time"
      min={min}
      max={max}
      step={step}
      className={styles.nativeInput}
      placeholder={placeholder}
    />
  );

  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        {...(validations ? { rules: validations } : {})}
        render={({ field }) => (
          <ControlledTimeField
            {...rest}
            name={name}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            value={formatTime(field.value)}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
    );
  }

  if (register) {
    const field = register(name, validations);
    return renderNativeInput(field);
  }

  return renderNativeInput();
};

const TimeInputWithLabelAndError = withLabelAndError(TimeInput);

export default TimeInputWithLabelAndError;
export { TimeInput };
